import { AddressType } from "@/app/types/address";
import { AttachmentType } from "@/app/types/attachment";
import { CartItemProductType, ClientCartItemType } from "@/app/types/cart";
import { DeliveryTime, DeliveryType } from "@/app/types/delivery";
import { ProductDetailType } from "@/app/types/product-site";
import { ClientTryCartItemType } from "@/app/types/tryCart";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

export type ServerTryCartStatus = string;

export type ServerCartItemType = {
	id: number;
	product_id: number;
	product_name: string;
	price: number;
	images: AttachmentType[];
	product: CartItemProductType;
	site_product: {
		id: number;
		slug: string;
		title: string;
	};
};

export type ServerTryCartType = {
	id: number;
	status: ServerTryCartStatus;
	unique: any;
	delivered_date: any;
	delivery_code: number | string;
	created_at: string;
	updated_at: string;
	shipper: any;
	address: AddressType | null;
	delivery_method: DeliveryType | null;
	items: ServerCartItemType[];
};

export type TryCartState = {
	serverTryCart: ServerTryCartType;
	clientTryCart: {
		items: ClientCartItemType[];
	};
	loading: boolean;
	deliveryMethod: DeliveryType | null;
	deliveryTime: DeliveryTime | null;
	fetchingCart: boolean;
	deliveryDate: string | null;
	address: AddressType | null;
};

const initialState: TryCartState = {
	serverTryCart: null,
	clientTryCart: {
		items: [],
	},
	fetchingCart: false,
	loading: false,
	deliveryMethod: null,
	deliveryTime: null,
	deliveryDate: null,
	address: null,
};

const tryCartSlice = createSlice({
	name: "tryCart",
	initialState,
	reducers: {
		startLoading(state) {
			state.loading = true;
		},
		stopLoading(state) {
			state.loading = false;
		},
		addToClientCart(state, action: PayloadAction<ClientTryCartItemType>) {
			const inStorage = localStorage.getItem("client_try_cart");

			if (inStorage) {
				state.clientTryCart.items = JSON.parse(inStorage);
			}
			state.clientTryCart.items.push({
				product: action.payload,
				qty: 1,
			});

			const key = "client_try_cart";
			const oldData = localStorage.getItem(key);
			const newData = JSON.stringify(action.payload);

			if (oldData !== newData) {
				const payload = state.clientTryCart?.items?.length ? JSON.stringify(state.clientTryCart.items) : null;
				localStorage.setItem(key, payload);
			}
		},
		removeFromClientCart(state, action: PayloadAction<ProductDetailType>) {
			state.clientTryCart.items = state.clientTryCart.items.filter((item) => item?.product?.product_id !== action?.payload?.product_id);
			localStorage.setItem("client_try_cart", JSON.stringify(state.clientTryCart.items));
		},
		updateTryCart(state, action: PayloadAction<any>) {
			state.serverTryCart = action.payload;
			const key = "server_try_cart";
			const oldData = localStorage.getItem(key);
			const newData = JSON.stringify(action.payload);
			if (oldData !== newData) {
				localStorage.setItem(key, JSON.stringify(action.payload));
			}
		},
		updateClientTryCart(state, action: PayloadAction<any>) {
			state.clientTryCart.items = action.payload;
		},
		updateTryCartDeliveryMethod(state, action: PayloadAction<DeliveryType>) {
			state.deliveryMethod = action.payload;
		},
		updateTryCartDeliveryTime(state, action: PayloadAction<DeliveryTime>) {
			state.deliveryTime = action.payload;
		},
		updateTryCartDeliveryDate(state, action: PayloadAction<string>) {
			state.deliveryDate = action.payload;
			if (!!state.deliveryTime) state.deliveryTime = null;
		},
		updateTryCartAddress(state, action: PayloadAction<AddressType>) {
			state.address = action.payload;
		},
		clearClientTryCart(state) {
			state.clientTryCart.items = [];
			localStorage.removeItem("client_try_cart");
		},
		clearServerCart(state) {
			state.serverTryCart = null;
		},
		clearTryCart: () => initialState,
		startTryCartFetching(state) {
			state.fetchingCart = true;
		},
		stopTryCartFetching(state) {
			state.fetchingCart = false;
		},
	},
});

export const {
	updateTryCart,
	updateClientTryCart,
	clearTryCart,
	clearClientTryCart,
	clearServerCart,
	startTryCartFetching,
	stopTryCartFetching,
	startLoading,
	stopLoading,
	addToClientCart,
	removeFromClientCart,
	updateTryCartDeliveryMethod,
	updateTryCartDeliveryTime,
	updateTryCartDeliveryDate,
	updateTryCartAddress,
} = tryCartSlice.actions;

export default tryCartSlice;
